// src/admin/pages/support/tabs/SupportRequestsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  searchAdminSupportRequestsForUser,
  getAdminSupportRequestDetail,
} from "../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* --------------------- utils --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function up(x) {
  return safeStr(x).toUpperCase();
}

function timeAgo(date) {
  const ms = Date.now() - date.getTime();
  if (!Number.isFinite(ms)) return "";
  const sec = Math.floor(ms / 1000);
  if (sec < 30) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon} month${mon === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mon / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

function fmtDT(x) {
  if (!x) return { pretty: "—", rel: "" };
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return { pretty: String(x), rel: "" };
  const pretty = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return { pretty, rel: timeAgo(d) };
}

function copyText(x) {
  const v = String(x || "");
  if (!v) return;
  try {
    navigator.clipboard?.writeText(v);
  } catch {}
}

function short(x, n = 52) {
  const s = String(x || "");
  if (!s) return "—";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function toNum(x) {
  if (x == null) return NaN;
  if (typeof x === "number") return Number.isFinite(x) ? x : NaN;
  const s = String(x).replace(/[, ]+/g, "").trim();
  if (!s) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

function fmtMoney(x) {
  const n = toNum(x);
  if (!Number.isFinite(n)) return x == null || safeStr(x) === "" ? "—" : String(x);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    // Fallback (no currency support)
    const s = n % 1 === 0 ? String(Math.trunc(n)) : String(Math.round(n * 100) / 100);
    return `₹${s}`;
  }
}

function firstFinite(...vals) {
  for (const v of vals) {
    const n = toNum(v);
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

function computeFareSummary(req) {
  const gst = firstFinite(req?.gst, req?.gstAmount);
  // Prefer explicit total fare fields first; fall back to sum of components if needed.
  let total = firstFinite(req?.fare, req?.totalFare, req?.paidAmount);

  if (!Number.isFinite(total)) {
    const parts = [
      req?.basePrice,
      req?.fuelCost,
      req?.commission,
      req?.apiCharges,
      req?.gst,
    ].map(toNum);
    const sum = parts.reduce((a, b) => (Number.isFinite(b) ? a + b : a), 0);
    total = sum > 0 ? sum : NaN;
  }

  const exGst =
    Number.isFinite(total) && Number.isFinite(gst) ? total - gst : NaN;

  return {
    totalFare: Number.isFinite(total) ? total : null,
    gst: Number.isFinite(gst) ? gst : null,
    fareExGst: Number.isFinite(exGst) ? exGst : null,
  };
}

/* --------------------- search helpers (client-side, best-effort) --------------------- */
function tokenizeQuery(q) {
  const s = safeStr(q);
  if (!s) return [];
  return s
    .split(/\s+/)
    .map((t) => safeStr(t))
    .filter(Boolean)
    .map((t) => t.replace(/^#/, ""))
    .map(up);
}

function rowMatchesQuery(r, q) {
  const tokens = tokenizeQuery(q);
  if (tokens.length === 0) return true;

  const requestId = r?.requestId ?? "";
  const planKey = r?.planKey ?? "";
  const status = r?.status ?? "";
  const paymentStatus = r?.paymentStatus ?? "";
  const paymentId = r?.paymentId ?? "";
  const matchBatchNo = r?.matchBatchNo ?? "";
  const finalBookingId = r?.finalBookingId ?? "";

  const offersTotal = r?.offersTotal ?? "";
  const offersAccepted = r?.offersAccepted ?? "";

  const pickupLocation = r?.pickupLocation ?? "";
  const destination = r?.destination ?? "";

  const phone = r?.userPhone ?? r?.phone ?? r?.mobile ?? "";

  const hay = [
    requestId,
    `#${requestId}`,
    planKey,
    status,
    paymentStatus,
    paymentId,
    `#${paymentId}`,
    matchBatchNo,
    `#${matchBatchNo}`,
    finalBookingId,
    `#${finalBookingId}`,
    offersTotal,
    offersAccepted,
    pickupLocation,
    destination,
    phone,
  ]
    .map((x) => up(x))
    .join(" | ");

  return tokens.every((t) => hay.includes(t));
}

/* --------------------- CSV export --------------------- */
function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/["\n,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => csvEscape(c.get(r))).join(",")
  );
  // Add BOM for Excel
  const blob = new Blob(["\uFEFF" + [header, ...lines].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* --------------------- UI atoms --------------------- */
function Tag({ label, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center border px-2 py-1 text-[11px] font-bold uppercase tracking-wider leading-none rounded-md",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}

function StatusTag({ status }) {
  const s = up(status);
  const tone =
    s === "LIVE"
      ? "good"
      : s === "PENDING_PAYMENT"
      ? "warn"
      : s === "FULFILLED"
      ? "good"
      : s === "EXPIRED"
      ? "bad"
      : s === "CANCELLED"
      ? "neutral"
      : "neutral";
  return <Tag label={s || "—"} tone={tone} />;
}

function PaymentTag({ status }) {
  const s = up(status);
  const tone =
    s === "PAID"
      ? "good"
      : s.includes("PENDING") || s === "CREATED"
      ? "warn"
      : s.includes("FAIL")
      ? "bad"
      : s.includes("REFUND")
      ? "bad"
      : "neutral";
  return <Tag label={s || "—"} tone={tone} />;
}

function IconRefresh({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66M20 4v6h-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSearch({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M16.5 16.5 21 21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconX({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconChevronRight({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconChevronDown({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDownload({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M12 3v10m0 0 4-4m-4 4-4-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 17v3h16v-3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
            {title}
          </div>
          {subtitle ? (
            <div className="mt-1 text-[13px] font-medium text-slate-500">
              {subtitle}
            </div>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function KV({ k, v, hint, action }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-3">
      <div className="col-span-12 md:col-span-4 text-[12px] font-bold uppercase tracking-wider text-slate-500">
        {k}
      </div>
      <div className="col-span-12 md:col-span-8 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words text-sm font-semibold text-slate-900">
            {v ?? "—"}
          </div>
          {hint ? (
            <div className="mt-1 text-[12px] font-medium text-slate-500">
              {hint}
            </div>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
}

function Button({
  children,
  onClick,
  disabled,
  tone = "soft",
  className = "",
  title,
}) {
  const cls =
    tone === "dark"
      ? "border-slate-900 bg-slate-900 text-white hover:opacity-95"
      : tone === "ghost"
      ? "border-transparent bg-transparent text-slate-700 hover:bg-slate-50"
      : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50";

  return (
    <button
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        "inline-flex h-9 items-center justify-center gap-2 border px-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed rounded-md",
        cls,
        className
      )}
    >
      {children}
    </button>
  );
}

function SelectField({ value, onChange, children, className = "", title }) {
  return (
    <div className={classNames("relative min-w-0", className)} title={title}>
      <select
        value={value}
        onChange={onChange}
        className={classNames(
          "h-10 w-full appearance-none border bg-white pl-3 pr-10 text-sm font-semibold text-slate-900 outline-none rounded-md border-slate-200 hover:bg-slate-50 focus:border-slate-300 truncate"
        )}
      >
        {children}
      </select>
      <IconChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
    </div>
  );
}

/* --------------------- request guidance (support script) --------------------- */
function offerCounts(offers) {
  const c = { total: 0, pending: 0, accepted: 0, declined: 0, expired: 0, other: 0 };
  const arr = Array.isArray(offers) ? offers : [];
  c.total = arr.length;
  for (const o of arr) {
    const s = up(o?.status);
    if (s === "PENDING") c.pending += 1;
    else if (s === "ACCEPTED") c.accepted += 1;
    else if (s === "DECLINED") c.declined += 1;
    else if (s === "EXPIRED") c.expired += 1;
    else c.other += 1;
  }
  return c;
}

function buildSupportScript(req, offers) {
  const status = up(req?.status);
  const pay = up(req?.paymentStatus);
  const oc = offerCounts(offers);

  if (!req) return { title: "Support script", bullets: ["Open a request to see guidance."] };

  if (status === "PENDING_PAYMENT") {
    return {
      title: "What to tell the user",
      bullets: [
        "Payment is pending. Ask the user to complete payment to activate matching (request becomes LIVE).",
        "If payment failed, ask them to retry payment or create a new booking if the old attempt is stuck.",
      ],
    };
  }

  if (status === "LIVE") {
    if (pay !== "PAID") {
      return {
        title: "What to tell the user",
        bullets: [
          "Request is LIVE but payment is not marked PAID yet. Ask the user to confirm payment and share the payment ID if available.",
          `Offers snapshot: ${oc.total} total (${oc.pending} pending, ${oc.accepted} accepted).`,
        ],
      };
    }
    return {
      title: "What to tell the user",
      bullets: [
        "Request is LIVE. Owners are receiving offers for matching vehicles.",
        `Offers snapshot: ${oc.total} total (${oc.pending} pending, ${oc.accepted} accepted, ${oc.declined} declined, ${oc.expired} expired).`,
        "If no offer is accepted before the deadline, the request will expire and the system should initiate refund (if paid).",
      ],
    };
  }

  if (status === "FULFILLED") {
    return {
      title: "What to tell the user",
      bullets: [
        "Request is FULFILLED. A booking is created for the accepted vehicle.",
        req?.finalBookingId ? `Booking ID: #${req.finalBookingId}` : "Booking ID is recorded on the request.",
        "Further updates will be on the booking flow (pickup/ride).",
      ],
    };
  }

  if (status === "EXPIRED") {
    return {
      title: "What to tell the user",
      bullets: [
        "Request is EXPIRED — no offer was accepted before the deadline.",
        pay === "PAID"
          ? "Since payment was PAID, refund should be initiated per policy. Check refund fields for status."
          : "Payment is not PAID; refund may not apply.",
        "If the user still needs a car, suggest placing a new booking.",
      ],
    };
  }

  if (status === "CANCELLED") {
    return {
      title: "What to tell the user",
      bullets: [
        "Request is CANCELLED. Matching is stopped.",
        req?.cancelReason ? `Cancel reason: ${req.cancelReason}` : "Cancel reason is not recorded.",
        pay === "PAID"
          ? "If payment was PAID, refund handling follows policy. Check refund fields."
          : "Payment is not PAID; refund may not apply.",
      ],
    };
  }

  return {
    title: "What to tell the user",
    bullets: [
      `Current status: ${status || "—"}`,
      `Payment status: ${pay || "—"}`,
      `Offers snapshot: ${oc.total} total (${oc.pending} pending, ${oc.accepted} accepted).`,
    ],
  };
}

/* --------------------- main --------------------- */
export default function SupportRequestsTab() {
  const { entityType = "users", entityId } = useParams();
  const enabled = entityType === "users";

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [paymentStatus, setPaymentStatus] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const searchMode = safeStr(q).length > 0;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState({ content: [], totalElements: 0, totalPages: 0, number: 0 });
  const [allRows, setAllRows] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [detail, setDetail] = useState(null);

  const suppressAutoFetchRef = useRef(false);
  const exportingRef = useRef(false);

  async function collectAllRows({ nextStatus = status, nextPay = paymentStatus, nextSize = size } = {}) {
    const PER_PAGE = Math.max(200, Number(nextSize || 25));
    const MAX_ROWS = 5000;

    const out = [];
    const seen = new Set();

    let p = 0;
    let totalPages = null;

    while (out.length < MAX_ROWS) {
      const data = await searchAdminSupportRequestsForUser({
        userId: entityId,
        q: undefined,
        status: nextStatus === "ALL" ? undefined : nextStatus,
        paymentStatus: nextPay === "ALL" ? undefined : nextPay,
        page: p,
        size: PER_PAGE,
      });

      const chunk = Array.isArray(data?.content) ? data.content : [];
      if (totalPages == null) {
        const tp = Number(data?.totalPages);
        totalPages = Number.isFinite(tp) && tp > 0 ? tp : null;
      }

      for (const r of chunk) {
        const rid = r?.requestId ?? null;
        const key = rid == null ? JSON.stringify(r) : String(rid);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(r);
        if (out.length >= MAX_ROWS) break;
      }

      if (!chunk.length) break;
      if (totalPages != null && p + 1 >= totalPages) break;
      if (totalPages == null && chunk.length < PER_PAGE) break;

      p += 1;
      if (p > 200) break;
    }

    return out;
  }

  async function fetchAllRowsForSearch({ nextStatus = status, nextPay = paymentStatus, nextSize = size } = {}) {
    const out = await collectAllRows({ nextStatus, nextPay, nextSize });
    setAllRows(out);
    return out;
  }

  async function fetchList({
    nextPage = page,
    nextSize = size,
    nextQ = q,
    nextStatus = status,
    nextPay = paymentStatus,
  } = {}) {
    if (!enabled) return;

    setLoading(true);
    setErr("");

    try {
      const qs = safeStr(nextQ);
      const isSearch = qs.length > 0;

      if (isSearch) {
        await fetchAllRowsForSearch({ nextStatus, nextPay, nextSize });
      } else {
        setAllRows(null);
        const data = await searchAdminSupportRequestsForUser({
          userId: entityId,
          q: qs || undefined,
          status: nextStatus === "ALL" ? undefined : nextStatus,
          paymentStatus: nextPay === "ALL" ? undefined : nextPay,
          page: nextPage,
          size: nextSize,
        });

        setRes({
          content: Array.isArray(data?.content) ? data.content : [],
          totalElements: Number(data?.totalElements ?? 0),
          totalPages: Number(data?.totalPages ?? 0),
          number: Number(data?.number ?? nextPage),
        });
      }
    } catch (e) {
      setErr(e?.message || "Failed to load requests");
      setRes({ content: [], totalElements: 0, totalPages: 0, number: 0 });
      setAllRows(null);
    } finally {
      setLoading(false);
    }
  }

  async function openDetail(requestId) {
    setDrawerOpen(true);
    setActiveId(requestId);
    setDetail(null);
    setDetailErr("");
    setDetailLoading(true);

    try {
      const data = await getAdminSupportRequestDetail(requestId);
      const out = data?.request ? data : data?.data?.request ? data.data : data;
      setDetail(out);
    } catch (e) {
      setDetailErr(e?.message || "Failed to load request detail");
    } finally {
      setDetailLoading(false);
    }
  }

  async function exportCsv() {
    if (exportingRef.current) return;
    exportingRef.current = true;

    try {
      // Export = current filters + query (best-effort; capped to 5k like your search scan)
      let rows = [];
      if (searchMode) {
        rows = Array.isArray(allRows) ? allRows : [];
        rows = rows.filter((r) => rowMatchesQuery(r, q));
      } else {
        rows = await collectAllRows({ nextStatus: status, nextPay: paymentStatus, nextSize: size });
      }

      const cols = [
        { label: "requestId", get: (r) => r?.requestId ?? "" },
        { label: "planKey", get: (r) => r?.planKey ?? "" },
        { label: "status", get: (r) => r?.status ?? "" },
        { label: "paymentStatus", get: (r) => r?.paymentStatus ?? "" },
        { label: "paymentId", get: (r) => r?.paymentId ?? "" },
        { label: "offersTotal", get: (r) => r?.offersTotal ?? "" },
        { label: "offersAccepted", get: (r) => r?.offersAccepted ?? "" },
        { label: "matchBatchNo", get: (r) => r?.matchBatchNo ?? "" },
        { label: "finalBookingId", get: (r) => r?.finalBookingId ?? "" },
        { label: "pickupLocation", get: (r) => r?.pickupLocation ?? "" },
        { label: "destination", get: (r) => r?.destination ?? "" },
        { label: "pickupAt", get: (r) => r?.pickupAt ?? "" },
        { label: "dropAt", get: (r) => r?.dropAt ?? "" },
        { label: "createdAt", get: (r) => r?.createdAt ?? "" },
        { label: "updatedAt", get: (r) => r?.updatedAt ?? "" },
        { label: "cancelledAt", get: (r) => r?.cancelledAt ?? "" },
      ];

      const nameBits = [
        "support_requests",
        `user_${entityId}`,
        status !== "ALL" ? status : "all",
        paymentStatus !== "ALL" ? paymentStatus : "all",
        searchMode ? "search" : "list",
      ];
      const filename = `${nameBits.join("_")}.csv`;

      downloadCsv(filename, rows, cols);
    } catch (e) {
      setErr(e?.message || "Export failed");
    } finally {
      exportingRef.current = false;
    }
  }

  useEffect(() => {
    if (!enabled) return;

    suppressAutoFetchRef.current = true;
    setQ("");
    setStatus("ALL");
    setPaymentStatus("ALL");
    setPage(0);
    setSize(25);
    setAllRows(null);

    fetchList({ nextPage: 0, nextQ: "", nextStatus: "ALL", nextPay: "ALL", nextSize: 25 }).finally(() => {
      suppressAutoFetchRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entityId]);

  useEffect(() => {
    if (!enabled) return;
    if (suppressAutoFetchRef.current) return;
    if (searchMode) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, page, size, status, paymentStatus, searchMode]);

  useEffect(() => {
    if (!enabled) return;
    if (suppressAutoFetchRef.current) return;
    if (!searchMode) return;
    fetchList({ nextPage: 0, nextQ: q });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, status, paymentStatus, searchMode]);

  useEffect(() => {
    if (!enabled) return;

    const t = setTimeout(() => {
      const qs = safeStr(q);
      setPage(0);

      if (!qs) {
        setAllRows(null);
        fetchList({ nextPage: 0, nextQ: "" });
        return;
      }
      if (!Array.isArray(allRows)) {
        fetchList({ nextPage: 0, nextQ: qs });
      }
    }, 350);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const baseRows = searchMode ? (Array.isArray(allRows) ? allRows : []) : res.content || [];

  const filteredRowsAll = useMemo(() => {
    if (!searchMode) return baseRows;
    return baseRows.filter((r) => rowMatchesQuery(r, q));
  }, [baseRows, q, searchMode]);

  const total = searchMode ? filteredRowsAll.length : Number(res.totalElements || 0);
  const totalPages = searchMode
    ? Math.max(1, Math.ceil(total / Number(size || 25)))
    : Math.max(1, Number(res.totalPages || 1));
  const safePage = Math.min(Math.max(0, page), totalPages - 1);

  const pageRows = useMemo(() => {
    if (!searchMode) return baseRows;
    const start = safePage * Number(size || 25);
    const end = start + Number(size || 25);
    return filteredRowsAll.slice(start, end);
  }, [searchMode, baseRows, filteredRowsAll, safePage, size]);

  const statusOptions = useMemo(
    () => ["ALL", "PENDING_PAYMENT", "LIVE", "FULFILLED", "CANCELLED", "EXPIRED"],
    []
  );
  const paymentOptions = useMemo(
    () => ["ALL", "PAID", "PENDING", "FAILED", "REFUNDED", "PARTIAL_REFUND", "CREATED"],
    []
  );

  const fromIdx = total === 0 ? 0 : safePage * Number(size || 25) + 1;
  const toIdx = total === 0 ? 0 : Math.min(total, (safePage + 1) * Number(size || 25));

  const guidance = useMemo(() => {
    const req = detail?.request || null;
    const offers = Array.isArray(detail?.offers) ? detail.offers : [];
    return buildSupportScript(req, offers);
  }, [detail]);

  return (
    <div className="min-w-0 space-y-4">
      {!enabled && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-extrabold text-slate-900">Coming soon</div>
          <div className="mt-1 text-[13px] font-medium text-slate-500">
            Requests support is enabled for <span className="font-semibold text-slate-900">Users</span>.
          </div>
        </div>
      )}

      {enabled && (
        <>
          <Section
            title="Requests"
            subtitle="Read-only support view: search, open detail, share accurate context with user."
            right={
              <div className="flex items-center gap-2">
                <Button
                  tone="soft"
                  disabled={loading}
                  onClick={() =>
                    fetchList({ nextPage: searchMode ? 0 : safePage, nextSize: size, nextQ: q })
                  }
                  className="px-3"
                >
                  <IconRefresh className="h-4 w-4 text-slate-700" />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>

                <Button
                  tone="soft"
                  disabled={loading}
                  onClick={exportCsv}
                  className="px-3"
                  title="Export current filtered results (CSV)"
                >
                  <IconDownload className="h-4 w-4 text-slate-700" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              </div>
            }
          >
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-center">
                <div className="min-w-0 lg:col-span-6">
                  <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 focus-within:border-slate-300">
                    <IconSearch className="h-4 w-4 shrink-0 text-slate-500" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search: requestId / paymentId / status / batch / booking / planKey / phone"
                      className="min-w-0 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                    />
                    {q ? (
                      <button
                        onClick={() => setQ("")}
                        className="shrink-0 rounded-md border border-slate-200 bg-white p-1 text-slate-600 hover:bg-slate-50"
                        title="Clear"
                        type="button"
                      >
                        <IconX className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="min-w-0 lg:col-span-3">
                  <SelectField
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(0);
                    }}
                    title="Request status"
                  >
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>
                        {s === "ALL" ? "All statuses" : s}
                      </option>
                    ))}
                  </SelectField>
                </div>

                <div className="min-w-0 lg:col-span-3">
                  <SelectField
                    value={paymentStatus}
                    onChange={(e) => {
                      setPaymentStatus(e.target.value);
                      setPage(0);
                    }}
                    title="Payment status"
                  >
                    {paymentOptions.map((s) => (
                      <option key={s} value={s}>
                        {s === "ALL" ? "All payments" : s}
                      </option>
                    ))}
                  </SelectField>
                </div>

                {/* ✅ Removed top "View/page" control (only keep in footer) */}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[13px] font-medium text-slate-500">
                  {loading
                    ? "Loading…"
                    : searchMode
                    ? `${pageRows.length} shown • ${total} matched • ${fromIdx}-${toIdx}`
                    : `${pageRows.length} shown • ${total} total`}
                </div>

                <div className="text-[12px] font-medium text-slate-400">
                  Tip: multi-term search works (example:{" "}
                  <span className="font-semibold text-slate-600">PAID 123</span>).
                </div>
              </div>

              {err ? (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
                  {err}
                </div>
              ) : null}
            </div>
          </Section>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-5 py-3">
              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
                Requests list
              </div>
            </div>

            {loading ? (
              <div className="px-5 py-10 text-sm font-semibold text-slate-600">
                Loading requests…
              </div>
            ) : pageRows.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <div className="text-sm font-semibold text-slate-900">No requests found</div>
                <div className="mt-1 text-[13px] font-medium text-slate-500">
                  Try adjusting filters or search query.
                </div>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[1200px]">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Request
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Plan key
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Status
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Payment
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Payment ID
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Offers
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Batch
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Final booking
                      </th>
                      <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Last update
                      </th>
                      <th className="px-5 py-3 text-right text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Open
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200">
                    {pageRows.map((r) => {
                      const lastEventAt = r.cancelledAt || r.updatedAt || r.createdAt || null;
                      const lastPretty = fmtDT(lastEventAt).pretty;

                      const offersTotal = Number(r.offersTotal ?? 0);
                      const offersAccepted = Number(r.offersAccepted ?? 0);

                      return (
                        <tr
                          key={String(r.requestId)}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => openDetail(r.requestId)}
                        >
                          <td className="px-5 py-4 align-middle">
                            <span className="tabular-nums text-[13px] font-semibold text-slate-900">
                              #{r.requestId}
                            </span>
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <span className="block max-w-[260px] truncate font-mono text-[13px] font-semibold text-slate-900">
                              {r.planKey ? String(r.planKey) : "—"}
                            </span>
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <StatusTag status={r.status} />
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <PaymentTag status={r.paymentStatus} />
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <span className="tabular-nums text-[13px] font-semibold text-slate-900">
                              {r.paymentId ?? "—"}
                            </span>
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <span className="tabular-nums text-[13px] font-semibold text-slate-900">
                              {offersTotal} / A{offersAccepted}
                            </span>
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <span className="tabular-nums text-[13px] font-semibold text-slate-900">
                              {r.matchBatchNo ?? "—"}
                            </span>
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <span className="tabular-nums text-[13px] font-semibold text-slate-900">
                              {r.finalBookingId ?? "—"}
                            </span>
                          </td>

                          <td className="px-5 py-4 align-middle">
                            <span className="text-[13px] font-semibold text-slate-900">
                              {lastPretty}
                            </span>
                          </td>

                          <td className="px-5 py-4 align-middle text-right">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(r.requestId);
                              }}
                              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                            >
                              View <IconChevronRight className="h-4 w-4 text-slate-500" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination footer — match your Ops footer spec */}
            <div className="px-5 py-3 border-t border-slate-200 bg-white flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-xs text-slate-500">
                {total === 0 ? (
                  <>No rows</>
                ) : (
                  <>
                    Showing{" "}
                    <span className="font-semibold text-slate-700 tabular-nums">
                      {fromIdx}-{toIdx}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-slate-700 tabular-nums">{total}</span>
                  </>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">View</span>
                  <div className="relative">
                    <select
                      value={String(size)}
                      onChange={(e) => {
                        setSize(Number(e.target.value));
                        setPage(0);
                      }}
                      className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {[10, 25, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </select>
                    <IconChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 pointer-events-none" />
                  </div>
                </div>

                <div className="text-sm text-slate-700">
                  Page <span className="font-semibold tabular-nums">{safePage + 1}</span> of{" "}
                  <span className="font-semibold tabular-nums">{totalPages}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    disabled={safePage <= 0 || loading}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    disabled={safePage + 1 >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                    className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Drawer */}
          {drawerOpen && (
            <div className="fixed inset-0 z-[55]">
              <div className="absolute inset-0 bg-black/25" onClick={() => setDrawerOpen(false)} />
              <div className="absolute right-0 top-0 h-full w-full max-w-3xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl">
                <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
                        Request detail
                      </div>
                      <div className="mt-1 truncate text-2xl font-extrabold tracking-tight text-slate-900">
                        Request <span className="tabular-nums">#{activeId}</span>
                      </div>

                      {detail?.request ? (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <StatusTag status={detail.request.status} />
                          <PaymentTag status={detail.request.paymentStatus} />
                          <Tag label={`BATCH ${detail.request.matchBatchNo ?? "—"}`} />
                          {detail.request.finalBookingId ? (
                            <Tag label={`FINAL ${detail.request.finalBookingId}`} />
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={() => openDetail(activeId)} tone="soft" disabled={detailLoading}>
                        <IconRefresh className="h-4 w-4 text-slate-700" /> Refresh
                      </Button>
                      <Button onClick={() => setDrawerOpen(false)} tone="soft">
                        Close
                      </Button>
                    </div>
                  </div>

                  {detailErr ? (
                    <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
                      {detailErr}
                    </div>
                  ) : null}
                </div>

                <div className="space-y-4 px-5 py-4">
                  {detailLoading ? (
                    <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-600">
                      Loading detail…
                    </div>
                  ) : !detail?.request ? (
                    <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-600">
                      No detail loaded.
                    </div>
                  ) : (
                    <>
                      {/* Guidance */}
                      <Section
                        title={guidance.title}
                        subtitle="Use these bullets as a safe, accurate script. Support is read-only."
                      >
                        <ul className="list-disc pl-5 space-y-2 text-[13px] font-medium text-slate-700">
                          {guidance.bullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      </Section>

                      {/* Summary */}
                      <Section title="Request summary" subtitle="Key information for support context.">
                        {(() => {
                          const req = detail.request || {};
                          const destArr = Array.isArray(req.destinations)
                            ? req.destinations.map((x) => String(x || "").trim()).filter(Boolean)
                            : [];

                          const finalDestination =
                            (req.destination && String(req.destination).trim()) ||
                            (destArr.length ? destArr[destArr.length - 1] : "") ||
                            "—";

                          const stops =
                            req.destination && destArr.length
                              ? destArr
                              : destArr.length > 1
                              ? destArr.slice(0, -1)
                              : [];

                          const created = fmtDT(req.createdAt);
                          const updated = fmtDT(req.updatedAt);
                          const pickupAt = fmtDT(req.pickupAt);
                          const dropAt = fmtDT(req.dropAt);

                          const fare = computeFareSummary(req);

                          return (
                            <>
                              <KV
                                k="Request ID"
                                v={`#${req.requestId ?? activeId}`}
                                action={
                                  <Button tone="soft" onClick={() => copyText(String(req.requestId ?? activeId))}>
                                    Copy
                                  </Button>
                                }
                              />
                              <KV k="Plan key" v={req.planKey || "—"} />
                              <KV k="Pickup location" v={req.pickupLocation || "—"} />
                              <KV k="Final destination" v={finalDestination} />

                              {stops.length > 0 ? (
                                <KV
                                  k="Destination stops"
                                  v={
                                    <div className="space-y-1">
                                      {stops.map((s, i) => (
                                        <div key={`${s}-${i}`} className="text-sm font-semibold text-slate-900">
                                          {i + 1}. {s}
                                        </div>
                                      ))}
                                    </div>
                                  }
                                  hint="Intermediate stops recorded for this request."
                                />
                              ) : null}

                              <KV k="Status" v={<StatusTag status={req.status} />} />
                              <KV k="Payment status" v={<PaymentTag status={req.paymentStatus} />} />
                              <KV k="Payment ID" v={req.paymentId ?? "—"} />
                              <KV k="Pickup at" v={pickupAt.pretty} hint={pickupAt.rel ? `(${pickupAt.rel})` : ""} />
                              <KV k="Drop at" v={dropAt.pretty} hint={dropAt.rel ? `(${dropAt.rel})` : ""} />
                              <KV k="Created at" v={created.pretty} hint={created.rel ? `(${created.rel})` : ""} />
                              <KV k="Updated at" v={updated.pretty} hint={updated.rel ? `(${updated.rel})` : ""} />
                              <KV k="Batch" v={req.matchBatchNo ?? "—"} />
                              <KV k="Final booking ID" v={req.finalBookingId ?? "—"} />

                              {req.cancelledAt ? (
                                <>
                                  <div className="my-2 h-px bg-slate-200" />
                                  <KV k="Cancelled at" v={fmtDT(req.cancelledAt).pretty} />
                                  <KV k="Cancelled by" v={req.cancelledBy ?? "—"} />
                                  <KV k="Cancel reason" v={req.cancelReason ?? "—"} />
                                </>
                              ) : null}

                              <div className="my-2 h-px bg-slate-200" />

                              {/* ✅ Simplified fare section (only 3 fields as requested) */}
                              <KV
                                k="Total fare"
                                v={fare.totalFare == null ? "—" : fmtMoney(fare.totalFare)}
                                hint="Total amount for this request (includes GST if applicable)."
                              />
                              <KV
                                k="Fare (excl. GST)"
                                v={fare.fareExGst == null ? "—" : fmtMoney(fare.fareExGst)}
                                hint="Computed as Total fare − GST."
                              />
                              <KV
                                k="GST amount"
                                v={fare.gst == null ? "—" : fmtMoney(fare.gst)}
                              />

                              {/* Keep refund fields for support context */}
                              <div className="my-2 h-px bg-slate-200" />
                              <KV k="Paid amount" v={req.paidAmount == null ? "—" : fmtMoney(req.paidAmount)} />
                              <KV k="Refunded amount" v={req.refundedAmount == null ? "—" : fmtMoney(req.refundedAmount)} />
                              <KV
                                k="Pending refund amount"
                                v={req.pendingRefundAmount == null ? "—" : fmtMoney(req.pendingRefundAmount)}
                              />
                              <KV k="Razorpay refund ID" v={req.razorpayRefundId ?? "—"} />
                              <KV k="Refund status" v={req.razorpayRefundStatus ?? "—"} />
                            </>
                          );
                        })()}
                      </Section>

                      {/* Offers */}
                      <Section title="Offers" subtitle="All offers generated for this request (read-only).">
                        {Array.isArray(detail.offers) && detail.offers.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[980px]">
                              <thead className="bg-slate-50">
                                <tr className="border-b border-slate-200">
                                  <th className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Match
                                  </th>
                                  <th className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Status
                                  </th>
                                  <th className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Batch
                                  </th>
                                  <th className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Vehicle
                                  </th>
                                  <th className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Owner
                                  </th>
                                  <th className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Driver
                                  </th>
                                  <th className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Times
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {detail.offers.map((o) => (
                                  <tr key={String(o.matchId)} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 text-sm font-extrabold text-slate-900 tabular-nums">
                                      #{o.matchId}
                                    </td>
                                    <td className="px-4 py-3">
                                      <StatusTag status={o.status} />
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 tabular-nums">
                                      {o.batchNo ?? "—"}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                                      {o.vehicle?.registrationNumber || o.vehicle?.regNo || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                                      {o.ownerName || "—"}{" "}
                                      <span className="tabular-nums text-slate-500">
                                        {o.ownerPhone ? `(${o.ownerPhone})` : ""}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 tabular-nums">
                                      {o.selectedDriverId ?? "—"}
                                    </td>
                                    <td className="px-4 py-3 text-[12px] font-medium text-slate-600">
                                      <div>
                                        Created:{" "}
                                        <span className="font-semibold text-slate-900">
                                          {fmtDT(o.createdAt).pretty}
                                        </span>
                                      </div>
                                      <div>
                                        Responded:{" "}
                                        <span className="font-semibold text-slate-900">
                                          {fmtDT(o.respondedAt).pretty}
                                        </span>
                                      </div>
                                      {o.note ? (
                                        <div className="mt-1 text-slate-500">Note: {short(o.note, 80)}</div>
                                      ) : null}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-sm font-medium text-slate-500">No offers for this request.</div>
                        )}
                      </Section>

                      {/* Events */}
                      <Section title="Events timeline" subtitle="Most recent events first. TraceId helps debugging.">
                        {Array.isArray(detail.events) && detail.events.length > 0 ? (
                          <div className="space-y-2">
                            {detail.events.map((e) => (
                              <div key={String(e.id)} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="text-sm font-extrabold text-slate-900">
                                    {e.eventType || "EVENT"}
                                    <span className="text-slate-400"> • </span>
                                    <span className="text-slate-700">{e.actorType || "—"}</span>
                                    {e.actorId ? (
                                      <span className="tabular-nums text-slate-500"> #{e.actorId}</span>
                                    ) : null}
                                  </div>
                                  <div className="text-[12px] font-medium text-slate-500">
                                    {fmtDT(e.createdAt).pretty}{" "}
                                    {fmtDT(e.createdAt).rel ? `(${fmtDT(e.createdAt).rel})` : ""}
                                  </div>
                                </div>

                                {e.message ? (
                                  <div className="mt-2 text-[13px] font-medium text-slate-700">{e.message}</div>
                                ) : null}

                                <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] font-medium text-slate-500">
                                  Trace:
                                  <span className="rounded-md border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-900 tabular-nums">
                                    {e.traceId || "—"}
                                  </span>
                                  <button
                                    type="button"
                                    className="rounded-md border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-900 hover:bg-slate-50"
                                    onClick={() => copyText(e.traceId)}
                                  >
                                    Copy
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm font-medium text-slate-500">No events recorded for this request.</div>
                        )}
                      </Section>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
